<script setup lang="ts">
import { useRafFn, useTimeoutFn } from '@vueuse/core'
import { ExperimentValue } from '@core/types'
import type { HeaderProps } from '#layers/uk/types'

const props = defineProps<HeaderProps>()

const links = useLinks()
const { isMobile } = useDevice()
const img = useImage()
const { mediaVideoUrl } = useGameAssets()

// Each cut exists in both orientations; phones get the real 9:16 file instead of a CDN crop.
function heroMedia(path: string, audio = false) {
    const file = `${path}-${isMobile ? 'portrait' : 'landscape'}`

    return {
        video: mediaVideoUrl(`${file}.mp4`, isMobile ? 480 : 1280, undefined, audio),
        poster: img(`${file}-poster.jpg`, { width: isMobile ? 480 : 1280, quality: 50 }, { provider: 'cloudflare' }),
    }
}

const VARIANT_MEDIA = {
    [ExperimentValue.A]: heroMedia('/landing_pages/uk/big-bait'),
    // B keeps its soundtrack: the hero starts it with sound wherever the browser allows, else at the first tap.
    [ExperimentValue.B]: heroMedia('/landing_pages/uk/happytiger/28/gold-mine-v28', true),
}

const variant = (props.type as keyof typeof VARIANT_MEDIA) in VARIANT_MEDIA ? (props.type as keyof typeof VARIANT_MEDIA) : ExperimentValue.A
const withSound = variant === ExperimentValue.B
const { video: VIDEO_SRC, poster: VIDEO_POSTER } = VARIANT_MEDIA[variant]
// Reuse the exact URL nuxt-image produced so the stylesheet and the poster
// attribute hit the same Cloudflare cache entry.
const POSTER_BG = `url(${VIDEO_POSTER})`
// A keeps the jungle board; B opens on the gold mine sign from design.
const VARIANT_BOARD = {
    [ExperimentValue.A]: {
        portrait: '/landing_pages/uk/happytiger/23/hero-mobile.jpg',
        landscape: '/landing_pages/uk/happytiger/23/hero-desktop.jpg',
    },
    [ExperimentValue.B]: {
        portrait: '/landing_pages/uk/happytiger/28/gold-mine-loader-mobile.webp',
        landscape: '/landing_pages/uk/happytiger/28/gold-mine-loader-desktop.webp',
    },
}
const BOARD = VARIANT_BOARD[variant]

// useDevice().isMobile is an SSR-resolved boolean (not a ref), so this computed
// is stable per request — no client resize reactivity, which is what we want.
const boardImage = computed(() =>
    isMobile ? BOARD.portrait : BOARD.landscape,
)

const pageLoaded = usePageLoaded()

const videoEl = useTemplateRef<HTMLVideoElement>('videoEl')

// ─── Phase state (drives template classes; no DOM mutation) ───────────────────
const animReady = ref(false) // cosmetic entrance stagger on the offer lockup
const veilFaded = ref(false) // phase-1 board fades out to reveal video
const videoRevealed = ref(false) // video wrap fades 0 → 1
const dimIn = ref(false) // dark bridge fades in before the board fades out
const dimOut = ref(false) // dark bridge fades away once video has resolved
const lifted = ref(false) // offer slides down into the lower third
// The video buffers under the board and starts, with its sound, as the board fades.
const { state: autoplayState, awaitingTap, play: playVideo, muted, setMuted } = useDeferredVideoAutoplay(videoEl, { reportAs: 'landing-hero', respectReducedMotion: true, sound: withSound, ready: videoRevealed })
const { observeCta, trackCtaClick } = useLandingHeroTracking(videoEl, lifted, autoplayState)
const barHidden = ref(false) // loading bar fades out at the transition
const barWidth = ref(0) // 0 → 100, RAF-driven

// ─── Timing (ms) ──────────────────────────────────────────────────────────────
const START_DELAY = 200
const BAR_DURATION = 4140 // continuous eased fill
const HOLD_MS = 4450 // total phase-1 hold (bar fill + a short beat)
const DIM_LEAD = 380 // dim bridge starts this long before the hold ends
const VEIL_FADE = 1300 // must match the board fade-out transition duration
const SLIDE_DELAY = 800 // extra beat after the hold before the offer slides down

useHead({
    link: [{ rel: 'preload', as: 'image', href: VIDEO_POSTER }],
    meta: [{ name: 'theme-color', content: '#0d1a2b' }],
    style: [
        {
            innerHTML:
                ':root { --nav-bg-color: transparent; --nav-background: none; --landing-page-bg-override: #0d1a2b; --sticky-terms-bg-override: #0d1a2b; --sticky-terms-color-override: rgba(255,255,255,0.6); }',
        },
    ],
})

// Cubic ease-in-out (smoothstep): smooth S-curve so the bar accelerates in and
// decelerates out instead of snapping linearly.
function easeInOut(t: number): number {
    return t * t * (3 - 2 * t)
}

let barStart = 0
const { pause: pauseBar, resume: resumeBar } = useRafFn(
    ({ timestamp }) => {
        if (!barStart) barStart = timestamp

        const elapsed = timestamp - barStart
        const t = Math.min(elapsed / BAR_DURATION, 1)
        barWidth.value = easeInOut(t) * 100

        // Start the dim bridge a beat before the hold ends, so the board never
        // pops straight to the video — bright board → dimmed board → video.
        if (elapsed >= HOLD_MS - DIM_LEAD) dimIn.value = true

        if (t >= 1) pauseBar()
    },
    { immediate: false },
)

const { authOpen } = useLandingPageAuth()
const introSkipped = ref(false)

// The signup sheet opens over the settled video, never over the
// phase-1 loading board, so an open dialog jumps the intro to its end state.
function skipIntro() {
    introSkipped.value = true
    pauseBar()
    barHidden.value = true
    veilFaded.value = true
    videoRevealed.value = true
    dimIn.value = false
    dimOut.value = true
    lifted.value = true
}

watch(authOpen, (open) => {
    if (open) skipIntro()
}, { immediate: true })

onMounted(() => {
    // All timers are scheduled here, inside the component's effect scope, so
    // useTimeoutFn / useRafFn auto-clean on unmount (e.g. mid-animation route
    // change). Delays are absolute, relative to mount.

    // Kick off the cosmetic entrance stagger and the loading-bar fill.
    useTimeoutFn(() => {
        animReady.value = true

        if (!introSkipped.value) resumeBar()
    }, START_DELAY)

    // Reveal: board fades out, video fades in, loading bar hides.
    useTimeoutFn(() => {
        barHidden.value = true
        veilFaded.value = true
        videoRevealed.value = true
    }, START_DELAY + HOLD_MS)

    // Fade the dim bridge away once the board fade-out has mostly resolved.
    useTimeoutFn(
        () => {
            dimIn.value = false
            dimOut.value = true
        },
        START_DELAY + HOLD_MS + VEIL_FADE * 0.6,
    )

    // Slide the offer down into the lower third, held a beat past the reveal.
    useTimeoutFn(
        () => {
            lifted.value = true
        },
        START_DELAY + HOLD_MS + SLIDE_DELAY,
    )
})
</script>

<template>
    <section class="ht-lp" aria-label="Welcome offer" data-lp-section="hero">
        <!-- LAYER 1: looping video background, buffering under the board so it starts the moment the board fades -->
        <div
            class="ht-lp__video"
            :class="{ 'ht-lp__video--revealed': videoRevealed }"
            aria-hidden="true"
        >
            <video
                v-if="pageLoaded"
                ref="videoEl"
                class="hero__video"
                loop
                :muted="!withSound"
                playsinline
                disableRemotePlayback
                decoding="async"
                preload="auto"
                :poster="VIDEO_POSTER"
                :src="VIDEO_SRC"
            />
        </div>

        <VideoPlayButton v-if="lifted && awaitingTap && !authOpen" @click="playVideo" />
        <!-- The sound carries on as set under the sheet, and the toggle moves to the dialog layer so it stays reachable there. -->
        <Teleport to="#teleports" :disabled="!authOpen">
            <VideoSoundButton
                v-if="withSound && lifted && !awaitingTap"
                class="ht-lp__sound"
                :muted="muted"
                :raised="authOpen"
                @click="setMuted(!muted)"
            />
        </Teleport>

        <!-- LAYER 2: persistent legibility scrim (always present) -->
        <div class="ht-lp__scrim" aria-hidden="true" />

        <!-- LAYER 3: phase-1 board (A jungle, B gold mine sign) — fades out to reveal the video -->
        <div
            class="ht-lp__veil"
            :class="{ 'ht-lp__veil--faded': veilFaded }"
            aria-hidden="true"
        >
            <NuxtImg
                class="ht-lp__veil-img"
                provider="cloudflare"
                preset="gameservice"
                :src="boardImage"
                sizes="sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw"
                :quality="75"
                fetchpriority="high"
                decoding="async"
                alt=""
            />
            <div class="ht-lp__veil-scrim" />
        </div>

        <!-- LAYER 4: dim bridge — eases the bright board → dark video jump -->
        <div
            class="ht-lp__dim"
            :class="{ 'ht-lp__dim--in': dimIn, 'ht-lp__dim--out': dimOut }"
            aria-hidden="true"
        />

        <!-- LAYER 10: offer lockup — always on top, always visible -->
        <div class="ht-lp__content">
            <div
                class="ht-offer"
                :class="{
                    'ht-offer--lifted': lifted,
                    'ht-offer--anim': animReady,
                }"
            >
                <p class="ht-offer__eyebrow">
                    New UK customers
                </p>

                <h1 class="ht-offer__headline">
                    We double
                    <span class="ht-offer__headline-accent">your deposit</span>
                </h1>

                <p class="ht-offer__subline">
                    <strong>100% Bonus</strong> up to <strong>£100</strong>
                </p>

                <NuxtLink
                    :ref="observeCta"
                    v-bind="links.signup.props()"
                    class="ht-offer__cta"
                    data-test="header-button-signup"
                    @click="trackCtaClick('hero')"
                >
                    Continue
                </NuxtLink>

                <div class="ht-offer__trust">
                    <span class="ht-offer__trust-item">
                        <span
                            class="ht-offer__stars"
                            aria-label="4 stars"
                        >★★★★</span>
                        <span>Great on Trustpilot</span>
                    </span>
                    <span class="ht-offer__trust-sep" aria-hidden="true" />
                    <span class="ht-offer__trust-item">
                        <span aria-hidden="true">⚡</span>
                        <span>Fast withdrawals</span>
                    </span>
                </div>

                <p class="ht-offer__tc">
                    T&amp;Cs apply — see below for details
                </p>

                <!-- Loading bar (phase 1 only) -->
                <div
                    class="ht-bar"
                    :class="{
                        'ht-bar--hidden': barHidden,
                        'ht-bar--gold': variant === ExperimentValue.B,
                    }"
                    aria-hidden="true"
                >
                    <div class="ht-bar__label">
                        Loading game…
                    </div>
                    <div class="ht-bar__track">
                        <div
                            class="ht-bar__fill"
                            :style="{ width: `${barWidth}%` }"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
// ─── Color tokens (from the UX mockup) ────────────────────────────────────────
$navy: #0d1a2b;
$yellow: #ffd400;
$orange-1: #ff7a00;
$easing-out: cubic-bezier(0.22, 1, 0.36, 1);

// ─── Hero wrapper ─────────────────────────────────────────────────────────────
.ht-lp {
    position: relative;
    width: 100%;
    min-height: 100dvh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: $navy;
    color: #fff;

    // Soft shadow rising up from the hero's bottom edge — reads as a dropshadow
    // cast upward, easing the seam where the header meets the terms band below
    // instead of a hard colour break. Sits above the media layers (z 1–4) but
    // below the sticky terms band (z-index 6, which overlaps the hero's bottom)
    // and the offer lockup (z-index 10), so neither gets darkened.
    &::after {
        content: '';
        position: absolute;
        inset: auto 0 0 0;
        height: 120px;
        z-index: 5;
        pointer-events: none;
        background: linear-gradient(to top, rgba(6, 15, 30, 0.6) 0%, transparent 100%);
    }
}

// Below the transparent nav. On the button itself, so the offset follows it into the dialog layer.
.ht-lp__sound {
    --video-sound-button-top: 84px;
}

// ─── Layer 1: video background ────────────────────────────────────────────────
// Starts at opacity 0 (hidden behind the board) and fades in at the reveal.
.ht-lp__video {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    opacity: 0;
    will-change: opacity;
    background-image: v-bind(POSTER_BG);
    background-size: cover;
    background-position: center;
    background-color: $navy;

    &--revealed {
        opacity: 1;
        transition: opacity 1.3s $easing-out;
    }

    .hero__video {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

// ─── Layer 2: persistent legibility scrim ─────────────────────────────────────
.ht-lp__scrim {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background:
        radial-gradient(ellipse 75% 40% at 50% 90%, rgba(6, 15, 30, 0.7) 0%, rgba(6, 15, 30, 0.3) 55%, transparent 80%),
        linear-gradient(to bottom, rgba(6, 15, 30, 0.3) 0%, transparent 18%);
}

// ─── Layer 3: phase-1 board (A jungle, B gold mine sign) ──────────────────────
.ht-lp__veil {
    position: absolute;
    inset: 0;
    z-index: 3;
    pointer-events: none;
    opacity: 1;
    overflow: hidden;
    will-change: opacity;

    &--faded {
        opacity: 0;
        transition: opacity 1.3s $easing-out;
    }
}

.ht-lp__veil-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: translateX(-10px) scale(1.1);
    transform-origin: center;

    @include tablet(md) {
        transform: translate(-20px, -10px);
    }
}

.ht-lp__veil-scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
        radial-gradient(
            ellipse 90% 45% at 50% 108%,
            rgba(5, 15, 35, 0.78) 0%,
            rgba(5, 15, 35, 0.35) 45%,
            transparent 68%
        ),
        linear-gradient(to bottom, rgba(5, 15, 35, 0.3) 0%, transparent 20%);
}

// ─── Layer 4: dim bridge ──────────────────────────────────────────────────────
.ht-lp__dim {
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(5, 15, 35, 0.72);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.7s ease-in;
    will-change: opacity;

    &--in {
        opacity: 1;
    }

    &--out {
        opacity: 0;
        transition: opacity 0.8s 0.3s ease-out;
    }
}

// ─── Layer 10: offer lockup ───────────────────────────────────────────────────
.ht-lp__content {
    position: relative;
    z-index: 10;
    isolation: isolate;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    flex: 1;
    pointer-events: none;
}

.ht-offer {
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0 20px;
    width: 100%;
    max-width: 82vw;

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: transform 0.9s $easing-out;

    // Slides down as a transform, so the lift is not a layout shift.
    &--lifted {
        max-width: 90vw;
        transform: translate(-50%, -50%) translateY(25vh);
        transform: translate(-50%, -50%) translateY(25dvh);
    }

    @include tablet(md) {
        max-width: min(50vw, 760px);
        top: 55%;

        &--lifted {
            transform: translate(-50%, -50%) translateY(20vh);
            transform: translate(-50%, -50%) translateY(20dvh);
        }
    }

    &__eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: rgba(10, 20, 10, 0.55);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 999px;
        padding: 4px 10px;
        margin-bottom: 9px;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(4px);

        &::before {
            content: '';
            display: inline-block;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: $orange-1;
            box-shadow: 0 0 6px 2px rgba(255, 122, 0, 0.7);
            animation: ht-pulse-dot 2s ease-in-out infinite;
        }
    }

    &__headline {
        margin: 0 0 4px;
        font-size: clamp(38px, 11vw, 64px);
        font-weight: 900;
        line-height: 0.88;
        letter-spacing: -0.04em;
        color: $yellow;
        text-transform: uppercase;
        white-space: nowrap;
        text-shadow:
            0 1px 0 rgba(0, 0, 0, 0.9),
            0 2px 0 rgba(0, 0, 0, 0.6),
            0 4px 20px rgba(0, 0, 0, 0.7),
            0 8px 40px rgba(0, 0, 0, 0.5);

        @include tablet(md) {
            font-size: clamp(48px, 5.2vw, 74px);
            margin-bottom: 5px;
        }
    }

    &__headline-accent {
        display: block;
        margin-top: 2px;
        color: #fff;
        font-size: 0.78em;
        letter-spacing: 0.01em;
        white-space: nowrap;
    }

    &__subline {
        margin: 0 0 12px;
        font-size: clamp(15px, 4.2vw, 21px);
        font-weight: 700;
        color: rgba(255, 255, 255, 0.97);
        letter-spacing: 0.01em;
        text-shadow:
            0 1px 0 rgba(0, 0, 0, 0.8),
            0 2px 12px rgba(0, 0, 0, 0.65),
            0 4px 24px rgba(0, 0, 0, 0.5);

        strong {
            color: #fff;
        }

        @include tablet(md) {
            font-size: clamp(17px, 2vw, 22px);
        }
    }

    &__cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        min-width: 230px;
        padding: 18px 100px;
        border: none;
        border-radius: 999px;
        background: linear-gradient(180deg, #ff8a1a 0%, #ff7a00 100%);
        color: #fff;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: 0.01em;
        text-decoration: none;
        text-shadow: 0 2px 0 rgba(0, 0, 0, 0.3);
        white-space: nowrap;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        box-shadow:
            0 4px 20px rgba(255, 122, 0, 0.55),
            0 1px 0 rgba(255, 255, 255, 0.15) inset;
        transition:
            transform 0.15s,
            box-shadow 0.15s;

        // Sheen sweep
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -80%;
            width: 60%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
            transform: skewX(-20deg);
            animation: ht-shimmer 3.5s ease-in-out 1.5s infinite;
        }

        &:hover {
            transform: translateY(-1px);
            box-shadow:
                0 6px 28px rgba(255, 122, 0, 0.72),
                0 1px 0 rgba(255, 255, 255, 0.2) inset;
        }

        &:active {
            transform: scale(0.97) translateY(1px);
        }
    }

    &__trust {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 11px;
        font-size: 11px;
        color: rgba(255, 255, 255, 0.55);
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
    }

    &__trust-item {
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
    }

    &__trust-sep {
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.25);
        flex-shrink: 0;
    }

    &__stars {
        color: $yellow;
        letter-spacing: -1px;
    }

    &__tc {
        margin-top: 9px;
        font-size: 10px;
        letter-spacing: 0.02em;
        color: rgba(255, 255, 255, 0.38);
    }
}

// ─── Phase-2 entrance stagger (cosmetic) ──────────────────────────────────────
.ht-offer--anim {
    .ht-offer__eyebrow {
        animation: ht-slide-in 0.5s 0.1s $easing-out both;
    }
    .ht-offer__headline {
        animation: ht-slide-in 0.6s 0.25s $easing-out both;
    }
    .ht-offer__subline {
        animation: ht-slide-in 0.6s 0.42s $easing-out both;
    }
    .ht-offer__cta {
        animation: ht-slide-in 0.6s 0.56s $easing-out both;
    }
    .ht-offer__trust {
        animation: ht-fade-in 0.6s 0.72s both;
    }
    .ht-offer__tc {
        animation: ht-fade-in 0.5s 0.85s both;
    }
}

// ─── Loading bar ──────────────────────────────────────────────────────────────
.ht-bar {
    width: 100%;
    max-width: 310px;
    margin: 20px auto 0;
    opacity: 1;
    transition: opacity 0.5s ease-out;

    &--hidden {
        opacity: 0;
        pointer-events: none;
    }

    &__label {
        margin-bottom: 6px;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        text-align: center;
        color: #fff;
    }

    &__track {
        width: 100%;
        height: 16px;
        background: rgba(0, 0, 0, 0.45);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 999px;
        overflow: hidden;
    }

    &__fill {
        height: 100%;
        width: 0;
        border-radius: 999px;
        background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, transparent 40%, transparent 100%),
            linear-gradient(90deg, #0a6cf0 0%, #2f9bff 45%, #66c4ff 100%);
        box-shadow:
            0 0 14px 4px rgba(60, 150, 255, 0.75),
            0 0 4px 1px rgba(150, 210, 255, 0.5);
        will-change: width;
    }

    // B's bar matches the gold of its mine board
    &--gold &__fill {
        background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, transparent 40%, transparent 100%),
            linear-gradient(90deg, #d4890a 0%, #f5b31f 45%, #ffe08a 100%);
        box-shadow:
            0 0 14px 4px rgba(245, 179, 31, 0.75),
            0 0 4px 1px rgba(255, 224, 138, 0.5);
    }
}

// ─── Keyframes ────────────────────────────────────────────────────────────────
@keyframes ht-pulse-dot {
    0%,
    100% {
        box-shadow: 0 0 6px 2px rgba(255, 122, 0, 0.7);
    }
    50% {
        box-shadow: 0 0 10px 4px rgba(255, 122, 0, 0.9);
    }
}

@keyframes ht-shimmer {
    0% {
        transform: translateX(0) skewX(-20deg);
        opacity: 0;
    }
    10% {
        opacity: 1;
    }
    60% {
        transform: translateX(350%) skewX(-20deg);
        opacity: 0;
    }
    100% {
        transform: translateX(350%) skewX(-20deg);
        opacity: 0;
    }
}

@keyframes ht-slide-in {
    from {
        transform: translateY(10px);
        opacity: 0.4;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes ht-fade-in {
    from {
        opacity: 0.2;
    }
    to {
        opacity: 1;
    }
}

// <StickyTerms> is a sibling of this component in the page, so scoped styles can't reach
// it. Every variant renders this hero now, so the override targets .landing-page itself.
:global(.landing-page .offer-terms--sticky) {
    background: $navy
        linear-gradient(180deg, rgba(13, 26, 43, 0) 0%, rgba(13, 26, 43, 0.85) 55%, rgba(13, 26, 43, 0.96) 100%);
    max-width: none;
}

:global(.landing-page .offer-terms--sticky.container) {
    max-width: none;
    width: 100%;
    padding-left: 0;
    padding-right: 0;
    margin-left: 0;
    margin-right: 0;
}

:global(.landing-page .offer-terms--sticky .offer-terms__text) {
    max-width: 1024px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 15px;
    padding-right: 15px;
}

.landing-page--auth-open .ht-lp__content {
    display: none;
}

.landing-page--auth-open .ht-lp__scrim {
    display: none;

    @include tablet {
        display: block;
    }
}
</style>
